import { useState, useEffect, useCallback, useRef } from 'react';
import { zafClient, loadUserFields, getCurrentUser, searchUsers } from '@services/Zendesk';
import type { User, UserField } from 'types/UserZendesk';
import { useUserContext } from '@context/UserContext';
import { formatPhoneInput } from './masks';

let userFieldsPromise: Promise<UserField[]> | null = null;
let userFieldsCache: UserField[] | null = null;
export async function loadUserFieldsOnce(): Promise<UserField[]> {
  if (userFieldsCache) return userFieldsCache;
  if (userFieldsPromise) return userFieldsPromise;
  
  userFieldsPromise = loadUserFields().then(fields => {
    userFieldsCache = fields;
    return fields;
  });
  return userFieldsPromise;
}

export function useCustomFields(initialFields?: {
  [key: string]: string | number | boolean | string[] | Date | RegExp | null
}) {
  const [customFields, setCustomFields] = useState<{
    [key: string]: string | number | boolean | string[] | Date | RegExp | null
  }>({});
  const [typeUser, setTypeUser] = useState<User[] | null>(null);
  const [typeAccount, setTypeAccount] = useState<string | null>(null);
  const hasLoadedRef = useRef(false);
  const { customFormFields, updateCustomFormField, currentTipoConta, setCurrentTipoConta, setCustomFormFields, setUserFields, userFields } = useUserContext();

  useEffect(() => {
    if (hasLoadedRef.current) return;
    hasLoadedRef.current = true;
    
    (async () => {
      try {
        if (!userFields || userFields.length === 0) {
          const fields = await loadUserFieldsOnce();
          setUserFields(fields);
        }
        
        const user = await getCurrentUser(zafClient);
        const alcada = await searchUsers(zafClient, `email:${user.email}`);
        setTypeUser(alcada);

        if (initialFields) {
          setCustomFields(initialFields);
          setCustomFormFields(initialFields);
        }

        if (currentTipoConta) {
          setCustomFields(prev => ({ ...prev, tipo_de_conta: currentTipoConta }));
        }
      } catch (error) {
        console.error('Erro ao buscar dados iniciais:', error);
      }
    })();
  }, []); 

  const handleCustomFieldChange = useCallback(
    async (fieldKey: string, value: string | boolean | number | string[] | Date | RegExp) => {
      let formattedValue = value;


      if (typeof value === "string" && fieldKey.toLowerCase().includes("cep__endereco")) {
        const digits = value.replace(/\D/g, '').slice(0, 8);
        let formattedCep = digits;
        if (digits.length > 5) {
          formattedCep = `${digits.slice(0, 5)}-${digits.slice(5)}`;
        }
        formattedValue = formattedCep;

        setCustomFields(prev => ({ ...prev, [fieldKey]: formattedCep }));

        if (digits.length === 8) {
          try {
            const res = await fetch(`https://viacep.com.br/ws/${digits}/json/`);
            const data = await res.json();

            if (!data.erro) {
              const bairro = data.bairro || '';
              const endereco = `${data.logradouro || ''} ${data.complemento || ''}`.trim();
              const cidade = data.localidade || data.localidade || '';
              const estado = data.uf || '';

              setCustomFields(prev => ({
                ...prev,
                [fieldKey]: formattedCep,
                bairro,
                endereco,
                cidade,
                estado
              }));

              updateCustomFormField(fieldKey, formattedCep);
              updateCustomFormField('bairro', bairro.slice(0, 60));
              updateCustomFormField('endereco_2', endereco.slice(0, 60));
              updateCustomFormField('cidade', cidade.slice(0, 60));
              updateCustomFormField('estado', estado.slice(0, 60));
            }
          } catch (error) {
            console.error("Erro ao buscar CEP:", error);
          }
        }
      }

      if (typeof value === "string" && fieldKey.toLowerCase().includes("cnpj")) {
        const onlyNumbers = value.replace(/\D/g, "").slice(0, 14);
        formattedValue = onlyNumbers.replace(
          /^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/,
          "$1.$2.$3/$4-$5"
        );
      }

      if (
        typeof value === "string" &&
        fieldKey.toLowerCase().includes("_pessoa_fisica__endereco_de_cobranca") &&
        typeUser && typeUser[0].role !== "admin"
      ) {
        formattedValue = value.slice(0, 40);
      }

      if (fieldKey === "tipo_de_conta") {
        setTypeAccount(value as string);
      }

      if (typeof value === "string" && fieldKey.toLowerCase().includes("celular")) {
        formattedValue = formatPhoneInput(value);
      }

      if (typeof value === "string" && fieldKey.toLowerCase().includes("_pessoa_fisica__idade__apenas_numeros_")) {
        const onlyNumbers = value.replace(/\D/g, "");
        formattedValue = onlyNumbers;
      }

      if (fieldKey === 'endereco_2' && typeof value === 'string') {
        formattedValue = value.slice(0, 60);
      }

      setCustomFields(prev => ({ ...prev, [fieldKey]: formattedValue }));
      updateCustomFormField(fieldKey, formattedValue);
      if (fieldKey === "tipo_de_conta") {
        setCurrentTipoConta(value as string);
      }
    },
    [setCurrentTipoConta, updateCustomFormField]
  );
  const tipoContas = customFormFields['tipo_de_conta'] || customFields['tipo_de_conta'] as string || null;
  const getVisibleFields = (selectedRole: string | null, isLegalEntity: boolean) => {
    return userFields
      .filter(field => {
        const title = field.title || '';

        if (title.startsWith('[Geral]')) {
          if (title.includes('[Geral] Idade (apenas números)')) return false;
          return true;
        }

        if (typeAccount === "pessoa_fisica") {
          const excludedFields = [
            'Data de nascimento',
            'Idade',
            'Razão Social',
            'CNPJ',
            'Endereço de entrega',
            'Bairro de entrega',
            'Complemento de entrega',
            'CEP de entrega',
            'Cognome',
            'ERP'
          ];

          if (excludedFields.some(fieldName => title.includes(fieldName))) {
            return false;
          }

          if (title.includes('CNPJ')) return false;
          if (title.includes('Razão social')) return false;
        }

        if (isLegalEntity && title.includes('Tipo de conta')) return false;
        if (title.includes('Perfil da conta')) return false;
        if (title.includes('Alçada do usuário')) return false;
        if (title.includes('CPF')) return false;
        if (title.includes('CNPJ')) return false;

        const rolePrefixMatch = title.match(/^\[(.*?)\]/);
        if (rolePrefixMatch) {
          const requiredRole = rolePrefixMatch[1].toLowerCase();
          if (selectedRole?.toLowerCase() === 'juiz' && requiredRole === 'advogado') {
            return true;
          }

          if (selectedRole?.toLowerCase() === 'posto') {
            if (title.includes('[Assistência Técnica] Status da assistência')) return true;
            if (title.includes('[Assistência Técnica] Linhas atendidas')) return true;
            if (title.includes('[Assistência Técnica] Família Produto')) return true;
          }

          return selectedRole?.toLowerCase() === requiredRole;
        }


        return true;
      });
  };

  return {
    customFields: customFormFields,
    handleCustomFieldChange,
    getVisibleFields,
    setCustomFields,
    typeAccount,
    tipoContas
  };
}